import { useQuery } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { ChainId } from '@ui/types/chain';
import { Nullable } from '@ui/types/utility';
import { useMemo } from 'react';
import { createPublicClient, http, PublicClient } from 'viem';
import { multicall } from 'viem/actions';
import { usePublicClient } from 'wagmi';

import { getPairsManagerFacet } from '../../contract-instances/getPairsManagerFacet';
import { toPairsV4 } from './toPairsV4';
import { PairsV4 } from './types';

export type { PairsV4 };
/**
 * @deprecated use `useTradePairInfos`
 */
export const usePairsV4 = <T = PairsV4[]>(params?: {
  select?: (data: PairsV4[] | undefined) => T;
}) => {
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const PairsManagerFacet = useMemo(() => {
    if (!client) return;

    return getPairsManagerFacet({
      chainId,
      publicClient: client,
    });
  }, [chainId, client]);

  return useQuery({
    select: params?.select,

    queryKey: ['PairsManagerFacet.pairsV4', chainId, client?.uid],
    queryFn: getFn({ chainId, client }),
    enabled: !!PairsManagerFacet,
  });
};

const getFn = ({
  chainId,
  client,
}: {
  chainId: ChainId;
  client: Nullable<PublicClient>;
}) => {
  return async () => {
    if (!client) {
      throw new Error('Client missing');
    }

    const noMulticallClient = createPublicClient({
      chain: client.chain,
      transport: http(client.transport.url),
      batch: {
        multicall: false,
      },
    });

    const contract = getPairsManagerFacet({
      chainId,
      publicClient: noMulticallClient,
    });

    const re = await contract.read.pairsV4({});

    return re.map((v) => toPairsV4(v));
  };
};

usePairsV4.getFn = getFn;
